import {
    DataGrid,
    DataGridHeader,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    DataGridHeaderCell,
    type TableColumnDefinition,
    createTableColumn,
} from "@fluentui/react-components";
import { ArrowUp, ArrowDown, Edit, Trash, Search } from "lucide-react";
import { useState, useMemo } from "react";
import { type Job } from "../types/job";
import { Link } from "react-router-dom";
import ConfirmDialog from "./ConfirmDialog";
import type { JobsTableProps } from "../types/job";
import { useTranslation } from 'react-i18next';

export default function JobsTable({ jobs, openModal, handleDelete, handleStatusToggle, isAdmin }: JobsTableProps) {
    const { t } = useTranslation();

    const [sortColumn, setSortColumn] = useState<keyof Job | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [selectedDescription, setSelectedDescription] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const pageSize = 6;

    const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; job: Job | null }>({
        isOpen: false,
        job: null
    });

    const filteredRows = useMemo(() =>
        jobs.filter(job =>
            Object.values(job).some(val =>
                String(val).toLowerCase().includes(globalFilter.toLowerCase())
            )
        ), [jobs, globalFilter]
    );

    const [statusConfirm, setStatusConfirm] = useState<{
        isOpen: boolean;
        job: Job | null;
    }>({
        isOpen: false,
        job: null,
    });

    const sortedRows = useMemo(() => {
        if (!sortColumn || !sortDirection) return filteredRows;
        return [...filteredRows].sort((a, b) => {
            if (sortColumn === "job_created_at") {
                const dateA = new Date(a.job_created_at).getTime();
                const dateB = new Date(b.job_created_at).getTime();
                return sortDirection === "asc"
                    ? dateA - dateB
                    : dateB - dateA;
            }
            const A = String(a[sortColumn] ?? "");
            const B = String(b[sortColumn] ?? "");
            return sortDirection === "asc"
                ? A.localeCompare(B)
                : B.localeCompare(A);
        });
    }, [filteredRows, sortColumn, sortDirection]);

    const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

    const toggleSort = (columnId: keyof Job) => {
        setSortColumn(columnId);
        setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    };

    const SortIcon = (columnId: string) =>
        sortColumn === columnId && (
            sortDirection === "asc"
                ? <ArrowUp size={14} />
                : <ArrowDown size={14} />
        );

    const columns: TableColumnDefinition<Job>[] = [
        createTableColumn({
            columnId: "job_title",
            renderHeaderCell: () => (
                <div className="flex gap-1 cursor-pointer" onClick={() => toggleSort("job_title")}>
                    {t('jobTable.title')} {SortIcon("job_title")}
                </div>
            ),
            renderCell: item => (
                <Link
                    to={`/jobs/${item.job_id}`}
                    className="text-primary font-semibold hover:underline transition-colors cursor-pointer"
                >
                    {item.job_title}
                </Link>
            ),
        }),
        createTableColumn({
            columnId: "job_description",
            renderHeaderCell: () => t('jobTable.description'),
            renderCell: item => {
                const desc = item.job_description || "";
                const short = desc.length > 20 ? desc.slice(0, 20).trimEnd() + "…" : desc;
                return (
                    <div className="text-sm flex items-center justify-between w-full">
                        <span>{short || "-"}</span>
                        {desc.length > 20 && (
                            <button
                                onClick={() => setSelectedDescription(desc)}
                                className="ml-2 text-primary text-xs font-semibold hover:underline whitespace-nowrap shrink-0"
                            >
                                {t('jobTable.readMore')}
                            </button>
                        )}
                    </div>
                );
            },
        }),
        createTableColumn({
            columnId: "job_function",
            renderHeaderCell: () => t('jobTable.function'),
            renderCell: item => (
                <div className="text-sm">
                    {item.job_function || "-"}
                </div>
            ),
        }),
        createTableColumn({
            columnId: "job_mandatory_skills",
            renderHeaderCell: () => t('jobTable.mandatorySkills'),
            renderCell: item => (
                <div
                    className="w-full overflow-hidden text-sm truncate"
                    title={item.job_mandatory_skills}
                >
                    {item.job_mandatory_skills || "-"}
                </div>
            ),
        }),
        createTableColumn({
            columnId: "job_primary_language",
            renderHeaderCell: () => t('jobTable.language'),
            renderCell: item => (
                <div className="text-sm">
                    {item.job_primary_language || "-"}
                </div>
            ),
        }),
        createTableColumn({
            columnId: "job_created_at",
            renderHeaderCell: () => (
                <div className="flex gap-1 cursor-pointer whitespace-nowrap" onClick={() => toggleSort("job_created_at")}>
                    {t('jobTable.createdOn')} {SortIcon("job_created_at")}
                </div>
            ),
            renderCell: item => (
                <div className="text-sm whitespace-nowrap">
                    {item.job_created_at
                        ? new Date(item.job_created_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })
                        : "-"}
                </div>
            ),
        }),
        createTableColumn({
            columnId: "job_status",
            renderHeaderCell: () => (
                <div
                    className="flex gap-1 cursor-pointer whitespace-nowrap"
                    onClick={() => toggleSort("job_status")}>
                    {t('jobTable.status')} {SortIcon("job_status")}
                </div>
            ),
            renderCell: item => {
                const isActive = item.job_status === "active";

                return (
                    <button
                        disabled={!isAdmin}
                        onClick={() =>
                            isAdmin && setStatusConfirm({ isOpen: true, job: item })
                        }
                        className={`px-2 py-1 rounded-full text-xs font-semibold transition-all
                            ${item.job_status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-700"
                            }
                            ${!isAdmin
                                ? "opacity-50 blur-[0.8px] cursor-not-allowed"
                                : "hover:bg-opacity-80"
                            }`}
                        title={!isAdmin ? t('jobTable.adminOnly') : ""}
                    >
                        {item.job_status === "active" ? t('jobTable.active') : t('jobTable.inactive')}
                    </button>
                );
            }
        }),
        createTableColumn({
            columnId: "actions",
            renderHeaderCell: () => t('jobTable.actions'),
            renderCell: item => (
                <div className="flex gap-2">
                    <button
                        disabled={!isAdmin}
                        onClick={() => isAdmin && openModal(item)}
                        className={`p-1 rounded transition-all
                            ${isAdmin
                                ? "bg-gray-100 hover:bg-gray-200 cursor-pointer"
                                : "bg-gray-100 opacity-50 blur-[0.8px] cursor-not-allowed"
                            }`}
                        title={!isAdmin ? t('jobTable.adminOnly') : ""}
                    >
                        <Edit size={16} />
                    </button>
                    <button
                        disabled={!isAdmin}
                        onClick={() => isAdmin && setDeleteConfirm({ isOpen: true, job: item })}
                        className={`p-1 rounded transition-all
                            ${isAdmin
                                ? "bg-red-100 hover:bg-red-200 text-red-700 cursor-pointer"
                                : "bg-red-100 text-red-400 opacity-50 blur-[0.8px] cursor-not-allowed"
                            }`}
                        title={!isAdmin ? t('jobTable.adminOnly') : ""}
                    >
                        <Trash size={16} />
                    </button>

                </div>
            )
        }),
    ];

    return (
        <>
            <div className="max-w-xs mb-2 relative">
                <Search className="absolute left-2 top-2.5 text-gray-400" size={16} />
                <input
                    className="pl-8 border rounded w-full py-2 outline-none text-sm"
                    placeholder="Search jobs..."
                    value={globalFilter}
                    onChange={e => { setPage(1); setGlobalFilter(e.target.value); }}
                />
            </div>

            <DataGrid
                className="w-full"
                items={paginatedRows}
                columns={columns}
                resizableColumns
                columnSizingOptions={{
                    job_title: { minWidth: 180, idealWidth: 220, defaultWidth: 200 },
                    job_description: { minWidth: 200, idealWidth: 280, defaultWidth: 250 },
                    job_function: { minWidth: 120, idealWidth: 150, defaultWidth: 140 },
                    job_mandatory_skills: { minWidth: 150, idealWidth: 200, defaultWidth: 180 },
                    job_primary_language: { minWidth: 100, idealWidth: 130, defaultWidth: 120 },
                    job_created_at: { minWidth: 110, idealWidth: 120, defaultWidth: 115 },
                    job_status: { minWidth: 90, idealWidth: 110, defaultWidth: 100 },
                    actions: { minWidth: 100, idealWidth: 120, defaultWidth: 110 }
                }}
            >
                <DataGridHeader>
                    <DataGridRow>
                        {({ renderHeaderCell }) => (
                            <DataGridHeaderCell
                                className="bg-gray-100 sticky top-0 z-20 border-b border-r border-gray-300 font-semibold text-gray-700 !whitespace-normal !break-words">
                                {renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>

                <DataGridBody>
                    {({ item }) => (
                        <DataGridRow>
                            {({ renderCell }) => <DataGridCell className="border-b border-r border-gray-200 align-top py-2">
                                {renderCell(item)}
                            </DataGridCell>}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>

            <div className="flex justify-end gap-3 mt-3">
                <button
                    className="px-3 py-1 border rounded text-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    Prev
                </button>
                <span className="text-sm">Page {page} of {Math.ceil(sortedRows.length / pageSize)}</span>
                <button
                    className="px-3 py-1 border rounded text-sm"
                    disabled={page === Math.ceil(sortedRows.length / pageSize)}
                    onClick={() => setPage(page + 1)}
                >
                    Next
                </button>
            </div>

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                title="Delete Job"
                message={`Are you sure you want to delete "${deleteConfirm.job?.job_title}"? This action cannot be undone.`}
                onConfirm={() => {
                    if (deleteConfirm.job) {
                        handleDelete(deleteConfirm.job.job_id);
                    }
                    setDeleteConfirm({ isOpen: false, job: null });
                }}
                onCancel={() => setDeleteConfirm({ isOpen: false, job: null })}
            />
            <ConfirmDialog
                isOpen={statusConfirm.isOpen}
                title="Change Job Status"
                message={`Are you sure you want to ${statusConfirm.job?.job_status === "active"
                    ? "deactivate"
                    : "activate"
                    } job "${statusConfirm.job?.job_title}"?`}
                confirmText={
                    statusConfirm.job?.job_status === "active"
                        ? "Deactivate"
                        : "Activate"
                }
                onConfirm={() => {
                    if (statusConfirm.job) {
                        handleStatusToggle(statusConfirm.job);
                    }
                    setStatusConfirm({ isOpen: false, job: null });
                }}
                onCancel={() =>
                    setStatusConfirm({ isOpen: false, job: null })
                }
            />
            {selectedDescription !== null && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => setSelectedDescription(null)}
                >
                    <div
                        className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 text-gray-900 animate-fadeIn"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-semibold text-gray-800">{t('jobTable.jobDescription')}</h3>
                            <button
                                className="text-gray-400 hover:text-gray-700 transition-colors text-xl leading-none"
                                aria-label="Close"
                                onClick={() => setSelectedDescription(null)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="max-h-80 overflow-y-auto pr-1">
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {selectedDescription}
                            </p>
                        </div>
                        <div className="flex justify-end mt-5">
                            <button
                                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-blue-800 transition-colors"
                                onClick={() => setSelectedDescription(null)}
                            >
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
